// Single app-wide machine-state subscription: <MachineStateProvider> owns the
// one SSE connection to /api/machine/stream plus flush batching, and
// useMachineState() is a thin context reader used by every page.
// See docs/design-decisions.md#ui-machine-state-provider
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import api from '../services/api';
import { isPageVisible, bumpSseEvent } from '../utils/renderMonitor';

const FLUSH_INTERVAL_MS = 10;
// No SSE bytes for this long means the stream is dead; the server sends an
// `hb` event every 5 s, so a live idle stream never gets near it.
const STALE_MS = 12_000;
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const DEFAULT_MACHINE_STATE = Object.freeze({
  state: 'idle',
  currentProgramId: null,
  activeRecipes: [],
  programStartRecipes: [],
  transitioningGates: [],
  completedTransitionGates: [],
  transitionStartRecipes: {},
  gatesAwaitingEmpty: {},
  lastUpdated: null,
});

const MachineStateContext = createContext(null);

// Per-tab cache of the last-known state. Seeding from it on remount avoids the
// brief "IDLE, 0 recipes" flash (which reads to an operator as a real stop);
// the initial fetch + SSE correct it within moments.
const STATE_CACHE_KEY = 'hmi_machine_state_cache_v1';

function loadCachedMachineState() {
  try {
    const raw = sessionStorage.getItem(STATE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.state !== 'string') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function saveCachedMachineState(state) {
  try {
    sessionStorage.setItem(STATE_CACHE_KEY, JSON.stringify(state));
  } catch (_) {
    // Quota/privacy-mode failures are non-fatal — worst case we fall back
    // to the old behaviour (default state until first fetch).
  }
}

/**
 * Provider that owns the single SSE connection. Mount once at the top
 * of the tree (above all routed pages) and all <useMachineState>
 * consumers will share the same data without spawning extra
 * EventSource instances.
 */
export function MachineStateProvider({ children }) {
  const [machineState, setMachineState] = useState(
    () => loadCachedMachineState() || DEFAULT_MACHINE_STATE
  );
  const [isConnected, setIsConnected] = useState(null);
  const [error, setError] = useState(null);
  const [orderUpdates, setOrderUpdates] = useState({});
  const [recipeBatchUpdates, setRecipeBatchUpdates] = useState({});
  const [recipeCompletions, setRecipeCompletions] = useState([]);
  const [batchLimitTransitions, setBatchLimitTransitions] = useState({});
  const [gateHandoffs, setGateHandoffs] = useState([]);

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  // Watchdog bookkeeping: timestamp of the LAST byte received on the SSE
  // (any event, including the server's 5 s `hb` heartbeat). See the
  // staleness check below for why this exists.
  const lastSseMessageAtRef = useRef(Date.now());
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  // Keep the per-tab cache in sync so a remount can seed from it (see
  // loadCachedMachineState above). Skip the initial default — never cache
  // the placeholder over a previously cached real state.
  useEffect(() => {
    if (machineState !== DEFAULT_MACHINE_STATE) {
      saveCachedMachineState(machineState);
    }
  }, [machineState]);

  const pendingRef = useRef({
    machine: null,
    recipeBatch: {},
    orderBatch: {},
    completions: [],
    transitions: {},
    handoffs: [],
    orderCompleted: {},
  });
  const flushTimerRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const response = await api.get('/machine/state');
      setMachineState(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    const doFlush = () => {
      flushTimerRef.current = null;
      const p = pendingRef.current;

      if (p.machine) {
        const data = p.machine;
        p.machine = null;
        setMachineState(prev => {
          const { lastUpdated: _a, ...prevCore } = prev;
          const { lastUpdated: _b, ...dataCore } = data;
          if (JSON.stringify(prevCore) === JSON.stringify(dataCore)) {
            return prev;
          }
          const next = { ...data };
          const preserve = (key) => {
            if (JSON.stringify(prev[key]) === JSON.stringify(data[key])) {
              next[key] = prev[key];
            }
          };
          preserve('activeRecipes');
          preserve('pausedGates');
          preserve('transitionStartRecipes');
          preserve('orderQueue');
          preserve('programStartRecipes');
          preserve('transitioningGates');
          preserve('completedTransitionGates');
          preserve('gateSnapshot');
          preserve('registeredTransitioningGates');
          preserve('gatesAwaitingEmpty');
          preserve('staleGates');
          return next;
        });
      }

      if (Object.keys(p.recipeBatch).length) {
        const batch = p.recipeBatch;
        p.recipeBatch = {};
        setRecipeBatchUpdates(prev => {
          const allSame = Object.entries(batch).every(([k, v]) =>
            prev[k] && JSON.stringify(prev[k]) === JSON.stringify(v)
          );
          return allSame ? prev : { ...prev, ...batch };
        });
      }

      if (Object.keys(p.orderBatch).length || Object.keys(p.orderCompleted).length) {
        const batch = p.orderBatch;
        const completed = p.orderCompleted;
        p.orderBatch = {};
        p.orderCompleted = {};
        setOrderUpdates(prev => {
          let next = { ...prev, ...batch };
          for (const [id, c] of Object.entries(completed)) {
            next[id] = { ...next[id], ...c };
          }
          if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
          return next;
        });
      }

      if (p.completions.length) {
        const items = p.completions;
        p.completions = [];
        setRecipeCompletions(prev => [...prev.slice(-(50 - items.length)), ...items]);
        const batchUpdates = {};
        for (const c of items) {
          batchUpdates[c.recipeKey] = { ...(batchUpdates[c.recipeKey] || {}), completedBatches: c.completedBatches, status: 'completed', lastUpdated: c.ts };
        }
        setRecipeBatchUpdates(prev => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(batchUpdates)) next[k] = { ...next[k], ...v };
          if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
          return next;
        });
        const keysToRemove = items.map(c => c.recipeKey);
        setBatchLimitTransitions(prev => {
          const next = { ...prev };
          for (const k of keysToRemove) delete next[k];
          return Object.keys(next).length !== Object.keys(prev).length ? next : prev;
        });
      }

      if (Object.keys(p.transitions).length) {
        const batch = p.transitions;
        p.transitions = {};
        setBatchLimitTransitions(prev => {
          const allSame = Object.entries(batch).every(([k, v]) =>
            prev[k] && JSON.stringify(prev[k]) === JSON.stringify(v)
          );
          return allSame ? prev : { ...prev, ...batch };
        });
        const batchUpdates = {};
        for (const [k, v] of Object.entries(batch)) {
          batchUpdates[k] = { batchLimitTransitioning: true, lastUpdated: v.startedAt };
        }
        setRecipeBatchUpdates(prev => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(batchUpdates)) next[k] = { ...next[k], ...v };
          if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
          return next;
        });
      }

      if (p.handoffs.length) {
        const items = p.handoffs;
        p.handoffs = [];
        setGateHandoffs(prev => [...prev.slice(-(10 - items.length)), ...items]);
      }
    };

    const scheduleFlush = () => {
      if (flushTimerRef.current) return;
      if (!isPageVisible()) return;
      flushTimerRef.current = setTimeout(doFlush, FLUSH_INTERVAL_MS);
    };

    const onVisibilityChange = () => {
      if (isPageVisible()) {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        doFlush();
        // Browsers throttle hidden tabs hard; on return, resync immediately if
        // nothing was received recently rather than waiting for the watchdog.
        if (Date.now() - lastSseMessageAtRef.current >= STALE_MS) {
          lastSseMessageAtRef.current = Date.now();
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          if (eventSourceRef.current) {
            try { eventSourceRef.current.close(); } catch (_) { /* noop */ }
            eventSourceRef.current = null;
          }
          fetchState();
          connectSSE();
        } else {
          // Even if the connection looks alive, events emitted while hidden
          // may have been dropped by the browser — cheap resync.
          fetchState();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const connectSSE = () => {
      try {
        let baseURL = process.env.REACT_APP_API_URL || '/api';
        baseURL = baseURL.replace(/\/api\/?$/, '');
        const url = `${baseURL}/api/machine/stream`;

        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          lastSseMessageAtRef.current = Date.now();
          setIsConnected(true);
          setError(null);
        };

        // `hb` heartbeat (every 5 s, no data) proves the connection is alive
        // so the staleness watchdog doesn't tear it down while the machine idles.
        eventSource.addEventListener('hb', () => {
          lastSseMessageAtRef.current = Date.now();
        });

        eventSource.onmessage = (event) => {
          try {
            lastSseMessageAtRef.current = Date.now();
            bumpSseEvent();
            const data = JSON.parse(event.data);
            const p = pendingRef.current;

            if (data.type === 'recipe_batch_update') {
              const key = data.recipeKey || data.recipeName;
              p.recipeBatch[key] = {
                completedBatches: data.completedBatches,
                requestedBatches: data.requestedBatches,
                recipeName: data.recipeName,
                orderId: data.orderId,
                gate: data.gate,
                gates: data.gates || [],
                lastUpdated: data.ts,
              };
            } else if (data.type === 'order_batch_update') {
              p.orderBatch[data.orderId] = {
                completedBatches: data.completedBatches,
                requestedBatches: data.requestedBatches,
                recipeName: data.recipeName,
                lastUpdated: data.ts,
              };
            } else if (data.type === 'order_completed') {
              p.orderCompleted[data.orderId] = {
                completedBatches: data.completedBatches,
                status: 'completed',
                lastUpdated: data.ts,
              };
            } else if (data.type === 'recipe_completed') {
              const key = data.recipeKey || data.recipeName;
              p.completions.push({
                recipeKey: key,
                recipeName: data.recipeName,
                completedBatches: data.completedBatches,
                requestedBatches: data.requestedBatches,
                orderId: data.orderId,
                gate: data.gate,
                gates: data.gates || [],
                ts: data.ts,
              });
            } else if (data.type === 'batch_limit_transition_started') {
              p.transitions[data.recipeKey] = {
                transitioning: true,
                recipeName: data.recipeName,
                completedBatches: data.completedBatches,
                requestedBatches: data.requestedBatches,
                gates: data.gates || [],
                startedAt: data.ts,
              };
            } else if (data.type === 'gate_handoff') {
              p.handoffs.push({
                gate: data.gate,
                fromRecipe: data.fromRecipe,
                fromRecipeKey: data.fromRecipeKey,
                toRecipe: data.toRecipe,
                handoffType: data.handoffType,
                assigned: data.assigned,
                needed: data.needed,
                ts: data.ts,
              });
            } else {
              p.machine = data;
            }

            scheduleFlush();
          } catch (err) {
            // Silent fail for parse errors
          }
        };

        eventSource.onerror = () => {
          setIsConnected(false);
          eventSource.close();
          reconnectTimeoutRef.current = setTimeout(() => {
            connectSSE();
          }, 5000);
        };
      } catch (err) {
        setError(err.message);
      }
    };

    fetchState();
    connectSSE();

    // CRITICAL: EventSource can die silently (onerror never fires); no bytes
    // for STALE_MS means dead — tear down, resync over HTTP, reconnect.
    // See docs/design-decisions.md#incident-2026-07-silent-sse-stall
    const staleCheck = setInterval(() => {
      if (!isPageVisible()) return; // background tabs are throttled anyway
      if (Date.now() - lastSseMessageAtRef.current < STALE_MS) return;
      lastSseMessageAtRef.current = Date.now(); // one recovery per timeout window
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        try { eventSourceRef.current.close(); } catch (_) { /* noop */ }
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      fetchState();
      connectSSE();
    }, 3_000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(staleCheck);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [fetchState]);

  // Prune stale entries from orderUpdates/recipeBatchUpdates every 60s
  useEffect(() => {
    const pruneTimer = setInterval(() => {
      const ms = machineStateRef.current;
      const activeRecipes = ms.activeRecipes || [];
      const orderQueue = ms.orderQueue || [];

      if (ms.state === 'idle') {
        setOrderUpdates(prev => Object.keys(prev).length ? {} : prev);
        setRecipeBatchUpdates(prev => Object.keys(prev).length ? {} : prev);
        return;
      }

      const activeOrderIds = new Set();
      const activeRecipeKeys = new Set();
      activeRecipes.forEach(r => {
        if (r.orderId) activeOrderIds.add(String(r.orderId));
        activeRecipeKeys.add(r.recipeName);
        if (r.recipeKey) activeRecipeKeys.add(r.recipeKey);
        if (r.orderId) activeRecipeKeys.add(`order_${r.orderId}`);
      });
      orderQueue.forEach(o => {
        if (o.orderId) activeOrderIds.add(String(o.orderId));
        if (o.recipeName) activeRecipeKeys.add(o.recipeName);
        if (o.orderId) activeRecipeKeys.add(`order_${o.orderId}`);
      });

      setOrderUpdates(prev => {
        const keys = Object.keys(prev);
        if (!keys.length) return prev;
        const next = {};
        for (const k of keys) {
          if (activeOrderIds.has(k)) next[k] = prev[k];
        }
        return keys.length === Object.keys(next).length ? prev : next;
      });

      setRecipeBatchUpdates(prev => {
        const keys = Object.keys(prev);
        if (!keys.length) return prev;
        const next = {};
        for (const k of keys) {
          if (activeRecipeKeys.has(k) || activeRecipeKeys.has(prev[k]?.recipeName)) {
            next[k] = prev[k];
          }
        }
        return keys.length === Object.keys(next).length ? prev : next;
      });
    }, 60000);
    return () => clearInterval(pruneTimer);
  }, []);

  const clearRecipeCompletions = useCallback(() => setRecipeCompletions([]), []);
  const clearGateHandoffs = useCallback(() => setGateHandoffs([]), []);

  const value = useMemo(() => ({
    state: machineState.state,
    activeRecipes: machineState.activeRecipes || EMPTY_ARRAY,
    currentProgramId: machineState.currentProgramId,
    programStartRecipes: machineState.programStartRecipes || EMPTY_ARRAY,
    transitioningGates: machineState.transitioningGates || EMPTY_ARRAY,
    completedTransitionGates: machineState.completedTransitionGates || EMPTY_ARRAY,
    transitionStartRecipes: machineState.transitionStartRecipes || EMPTY_OBJECT,
    transitionOldProgramId: machineState.transitionOldProgramId || null,
    registeredTransitioningGates: machineState.registeredTransitioningGates || EMPTY_ARRAY,
    orderQueue: machineState.orderQueue || EMPTY_ARRAY,
    gateSnapshot: machineState.gateSnapshot || EMPTY_ARRAY,
    pausedGates: machineState.pausedGates || EMPTY_ARRAY,
    gatesAwaitingEmpty: machineState.gatesAwaitingEmpty || EMPTY_OBJECT,
    // Gates holding a leftover incomplete batch while idle/stopped
    // (informational only — Start is never blocked by these).
    staleGates: machineState.staleGates || EMPTY_ARRAY,
    weightTareG: machineState.weightTareG ?? 0,
    lastUpdated: machineState.lastUpdated,
    orderUpdates,
    recipeBatchUpdates,
    recipeCompletions,
    clearRecipeCompletions,
    batchLimitTransitions,
    gateHandoffs,
    clearGateHandoffs,
    isConnected,
    error,
    refetch: fetchState,
  }), [
    machineState,
    orderUpdates,
    recipeBatchUpdates,
    recipeCompletions,
    batchLimitTransitions,
    gateHandoffs,
    isConnected,
    error,
    fetchState,
    clearRecipeCompletions,
    clearGateHandoffs,
  ]);

  return (
    <MachineStateContext.Provider value={value}>
      {children}
    </MachineStateContext.Provider>
  );
}

const DEFAULT_HOOK_RETURN = Object.freeze({
  state: 'idle',
  activeRecipes: EMPTY_ARRAY,
  currentProgramId: null,
  programStartRecipes: EMPTY_ARRAY,
  transitioningGates: EMPTY_ARRAY,
  completedTransitionGates: EMPTY_ARRAY,
  transitionStartRecipes: EMPTY_OBJECT,
  transitionOldProgramId: null,
  registeredTransitioningGates: EMPTY_ARRAY,
  orderQueue: EMPTY_ARRAY,
  gateSnapshot: EMPTY_ARRAY,
  pausedGates: EMPTY_ARRAY,
  gatesAwaitingEmpty: EMPTY_OBJECT,
  staleGates: EMPTY_ARRAY,
  weightTareG: 0,
  lastUpdated: null,
  orderUpdates: EMPTY_OBJECT,
  recipeBatchUpdates: EMPTY_OBJECT,
  recipeCompletions: EMPTY_ARRAY,
  clearRecipeCompletions: () => {},
  batchLimitTransitions: EMPTY_OBJECT,
  gateHandoffs: EMPTY_ARRAY,
  clearGateHandoffs: () => {},
  isConnected: null,
  error: null,
  refetch: async () => {},
});

/**
 * Reads the current machine state from the global provider.
 * @param {Object} [options]
 * @param {boolean} [options.disabled=false] - Return the static default shape
 *   without subscribing (for components taking a machineStateOverride prop).
 *   A legacy `connectionOnly` option is still accepted but is a no-op with the
 *   shared SSE; the full shape is always returned.
 * @returns {Object} Machine state, live update maps, connection status, refetch.
 */
export function useMachineState({ disabled = false } = {}) {
  const ctx = useContext(MachineStateContext);
  if (disabled) return DEFAULT_HOOK_RETURN;
  if (!ctx) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[useMachineState] called outside <MachineStateProvider>. Returning default state.'
      );
    }
    return DEFAULT_HOOK_RETURN;
  }
  return ctx;
}

export default useMachineState;
